// ── Plan catalog (display only) ───────────────────────────────────────────────
// Mirrors lib/plans.js on the server — CRA can't import from outside src/, so
// this small table is duplicated here purely so the UI can show prices before
// checkout. The server never trusts these numbers; it re-derives price and
// days-granted from its own copy of this table using the plan id alone.
// Keep this in sync with lib/plans.js whenever pricing changes.
//
// playProductId mirrors lib/plans.js's field of the same name — it's what
// src/firebase/playBilling.js asks the Play Billing Library to fetch/purchase
// for a given plan. Google is the source of truth for the price shown for a
// Play purchase (it localizes automatically), so php/usd above are NOT used
// on the native Android purchase flow — only for PayMongo/Lemon Squeezy.
export const PLANS = {
  weekly:  { days: 7,   label: 'Weekly',  php: 49,   usd: 2.99, playProductId: 'clarity_premium_weekly' },
  monthly: { days: 30,  label: 'Monthly', php: 149,  usd: 4.99, playProductId: 'clarity_premium_monthly' },
  yearly:  { days: 365, label: 'Yearly',  php: 999,  usd: 29.99, playProductId: 'clarity_premium_yearly' },
};

export const PLAN_ORDER = ['weekly', 'monthly', 'yearly'];

// Lifetime Premium — pay once, Premium for good. Mirrors LIFETIME_PLAN in
// lib/plans.js; kept out of PLANS because it has no day count. Sold on every
// rail: a one-time Play product on Android (price shown comes from Play), a
// one-time PayMongo checkout (php) and a one-time Lemon Squeezy variant (usd).
export const LIFETIME = { id: 'lifetime', label: 'Lifetime', php: 1999, usd: 59.99, playProductId: 'clarity_premium_lifetime' };

// Price after a purchasing-power discount (see lib/ppp-discount.js), in dollars.
// Mirrors how Lemon Squeezy rounds: the discount is rounded to the nearest cent
// and subtracted, so $29.99 at 50% off is $14.99 (a $15.00 discount).
export function discountedUsd(usd, percentOff) {
  const cents = Math.round(usd * 100);
  const off = Math.round((cents * percentOff) / 100);
  return (cents - off) / 100;
}

// ── Purchase-page extras ──────────────────────────────────────────────────────
// `amounts` is { [planId]: { value, currency } } in whatever currency the page
// is showing (Play's own amounts on Android, PHP or USD on the web).

// How much Yearly saves over paying Monthly for 12 months, in whole percent,
// rounded DOWN so the badge never overstates it. 0 = don't show a badge.
export function yearlySavingsPercent(amounts) {
  const m = amounts?.monthly;
  const y = amounts?.yearly;
  if (!m || !y || m.currency !== y.currency || !(m.value > 0) || !(y.value > 0)) return 0;
  const pct = Math.floor((1 - y.value / (m.value * 12)) * 100 + 1e-9);
  return pct > 0 ? pct : 0;
}

// One formatter per currency: building an Intl.NumberFormat is slow the
// first time, and this runs on every render of the purchase page.
const perMonthFormatters = {};
function currencyFormatter(currency) {
  if (!perMonthFormatters[currency]) {
    perMonthFormatters[currency] = new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
  }
  return perMonthFormatters[currency];
}

// The Yearly price spread over 12 months, e.g. "₱83.25". Null if unknown.
export function yearlyPerMonthLabel(amounts) {
  const y = amounts?.yearly;
  if (!y || !(y.value > 0) || !y.currency) return null;
  try {
    return currencyFormatter(y.currency).format(Math.floor((y.value / 12) * 100) / 100);
  } catch {
    return null;
  }
}
